/** Opt-in browser diagnostics for intermittent inventory input failures.
 * Enable in DevTools with window.__itemClickDebug.enabled = true;
 * see docs/browser-debugging.md for capture/export instructions.
 * Strings are snapshotted immediately so copied console output never shows
 * a later mutation. Only browser input and already-visible item state enter
 * the bounded buffer; packet logging is limited to item commands. */
import { ClientOp } from "@pd/shared";
import type { ClientPacket, Observation } from "@pd/shared";

declare global {
  interface Window {
    __itemClickDebug?: { enabled: boolean; dump(): string; clear(): void };
  }
}

const lines: string[] = [];
let enabled = false;
let lineNumber = 0;
let gesture = 0;
let tick: number | null = null;
let lastTickAt: number | null = null;
let stateSignature = "";
let latestState: unknown = null;

export function traceItemClick(stage: string, data: unknown = null): void {
  if (!enabled) return;
  try {
    const now = performance.now();
    const line = `[item-click] ${JSON.stringify({ n: ++lineNumber, ms: Math.round(now), tick,
      tickAgeMs: lastTickAt === null ? null : Math.round(now - lastTickAt), gesture, stage, data })}`;
    lines.push(line);
    if (lines.length > 500) lines.shift();
    console.log(line);
  } catch { /* Diagnostics must never interrupt input or the socket feed. */ }
}

function targetInfo(target: EventTarget | null): unknown {
  if (!(target instanceof Element)) return null;
  const cell = target.closest("[data-slot]");
  return { tag: target.tagName, cls: target.getAttribute("class"), slot: cell?.getAttribute("data-slot"),
    panel: target.closest("#inventory-panel, #equipment-panel")?.id ?? null, connected: target.isConnected };
}

export function installItemClickDebug(): void {
  if (window.__itemClickDebug) return;
  window.__itemClickDebug = {
    get enabled() { return enabled; },
    set enabled(value: boolean) {
      if (enabled === value) return;
      enabled = value;
      if (enabled) traceItemClick("debug-ready", { version: "item-click-v4", url: location.origin,
        bundle: Array.from(document.scripts).map(script => script.src).find(src => src.includes("bundle")) ?? null,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        help: "After a missed click, run copy(window.__itemClickDebug.dump()) in the Console and paste the result." });
    },
    dump() { traceItemClick("capture", latestState); return lines.join("\n"); },
    clear() { lines.length = 0; },
  };
  let inspecting = false;
  const inPanel = (ev: MouseEvent): boolean => {
    if (ev.target instanceof Element && ev.target.closest("#inventory-panel, #equipment-panel")) return true;
    return ["inventory-panel", "equipment-panel"].some(id => {
      const el = document.getElementById(id);
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return ev.clientX >= r.left && ev.clientX < r.right && ev.clientY >= r.top && ev.clientY < r.bottom;
    });
  };
  for (const type of ["pointerdown", "mousedown", "pointerup", "mouseup", "click", "pointercancel", "contextmenu"] as const) {
    document.addEventListener(type, (ev: MouseEvent) => {
      if (!enabled) return;
      if (type === "pointerdown") {
        inspecting = inPanel(ev);
        if (inspecting) gesture++;
      }
      if (!inspecting && !inPanel(ev)) return;
      traceItemClick(`dom-${type}`, { x: ev.clientX, y: ev.clientY, button: ev.button, buttons: ev.buttons,
        detail: ev.detail, trusted: ev.isTrusted, prevented: ev.defaultPrevented,
        pointerType: "pointerType" in ev ? ev.pointerType : null, target: targetInfo(ev.target),
        hits: document.elementsFromPoint(ev.clientX, ev.clientY).slice(0, 4).map(targetInfo) });
      if (type === "pointerdown") queueMicrotask(() => {
        if (ev.defaultPrevented) traceItemClick("dom-pointerdown-prevented", { target: targetInfo(ev.target) });
      });
    }, true);
  }
  document.addEventListener("keydown", ev => {
    if (!enabled || !(ev.target instanceof Element) || !ev.target.closest("#inventory-panel, #equipment-panel")) return;
    if (ev.key !== "Enter" && ev.key !== " ") return;
    gesture++;
    traceItemClick("dom-keydown", { key: ev.key, target: targetInfo(ev.target) });
  }, true);
  window.addEventListener("blur", () => traceItemClick("window-blur"));
  window.addEventListener("focus", () => traceItemClick("window-focus"));
}

export function traceItemCommand(packet: ClientPacket, readyState: number | undefined): void {
  if (!enabled) return;
  switch (packet.op) {
    case ClientOp.EquipItem: case ClientOp.UnequipItem: case ClientOp.UseItem:
    case ClientOp.DropItem: case ClientOp.ReorderInventory: case ClientOp.DropKey:
    case ClientOp.PlaceGatestone: case ClientOp.GenerateGatestone:
      break;
    case ClientOp.Interact:
      if (!["item", "sack", "key", "stone"].includes(packet.kind)) return;
      break;
    default: return;
  }
  traceItemClick(readyState === 1 ? "command-send" : "command-skipped-socket-not-open", { packet, readyState: readyState ?? null });
}

export function traceItemObservation(obs: Observation, meId: number | undefined, atTick: number, events: unknown[]): void {
  if (!enabled) return;
  tick = atTick;
  lastTickAt = performance.now();
  const me = obs.players.find(player => player.id === meId);
  latestState = me ? { playerId: me.id, alive: me.alive, items: me.items, keys: me.keys,
    carried: me.carried.map(stone => ({ id: stone.id, kind: stone.kind })), equipment: me.equipment } : { playerId: meId, missing: true };
  const signature = JSON.stringify(latestState);
  if (signature !== stateSignature) {
    stateSignature = signature;
    traceItemClick("server-item-state", latestState);
  }
  for (const event of events) {
    if (!event || typeof event !== "object") continue;
    const e = event as Record<string, unknown>;
    if (e.playerId !== meId || typeof e.type !== "string") continue;
    if (!["invalid", "item-equipped", "item-unequipped", "item-dropped", "drop-picked", "consumable-used",
      "sack-withdrawn", "key-found", "gatestone-generated", "gatestone-placed", "gatestone-picked"].includes(e.type)) continue;
    traceItemClick("server-item-event", e);
  }
}
